import React, { FC } from 'react';
import sanitizeHtml from 'sanitize-html';
import mustache from 'mustache';
import { useSettings } from '../../../../hooks/useSettings';
import {
  useAnalyticsPost,
  NarvarClickTypes,
  NarvarClickSubTypes,
  NarvarEventComponents,
  NarvarEventTypes,
} from '../../../../hooks/Analytics';
import { Item } from '../../../../../../__generated__/loma';
import Paragraph from '../../../ui/Paragraph/Paragraph';
import { SmallHeader } from '../../../ui/Header/Header';
import ProductThumbnail from '../../../ui/ProductThumbnail';
import styles from './ItemTile.module.css';
import { clsx } from 'clsx';

const renderOptions = { escape: (x: string): string => x };

interface ItemTileProps {
  itemInfo: Item;
  itemTextTemplates: Record<string, unknown>;
  featureName?: string;
}

// do not allow html tags in item attribute text
const safetyHtml = (text: string) => sanitizeHtml(text, { allowedTags: [] });

const ItemTile: FC<ItemTileProps> = ({
  itemInfo,
  itemTextTemplates,
  featureName,
}) => {
  const logEvent = useAnalyticsPost();
  const { itemsInThisShipment } = useSettings();
  const { imageUrl: imageUrlTemplate = '', url: itemUrlTemplate = '' } =
    itemsInThisShipment ?? {};

  // Currently the "hack" to not show the product images is setting the
  // image template setting to ' '. In an upcoming refresh of the items
  // in this shipment component settings, there will be a boolean flag
  // we'll be able to check instead of looking for ' '.
  const hideProductImage = imageUrlTemplate === ' ';

  const { price } = itemInfo;
  const tempPrice = price;
  let itemInfoPriceFormatted = itemInfo;
  // check if price contains any possible currency signs, and if not,
  // display in two-decimal format. otherwise, leave it as is.
  const alreadyFormatted =
    tempPrice && tempPrice.replaceAll(/[.,]/g, '').match(/[\D]/);

  if (price && !alreadyFormatted) {
    const priceNumber = parseFloat(price);

    // TODO: make this configurable and switch to 'currenty' options
    const priceFormatter = Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const formattedPrice = priceFormatter.format(priceNumber);
    itemInfoPriceFormatted = {
      ...itemInfo,
      price: formattedPrice,
    };
  }

  const itemTitle =
    itemTextTemplates.itemsInShipmentItemTitle &&
    typeof itemTextTemplates.itemsInShipmentItemTitle === 'string'
      ? mustache.render(
          itemTextTemplates.itemsInShipmentItemTitle,
          itemInfoPriceFormatted,
          undefined,
          renderOptions,
        )
      : '';

  let thumbnail;
  if (!hideProductImage) {
    thumbnail = (
      <ProductThumbnail
        productInfo={itemInfoPriceFormatted}
        urlTemplate={itemUrlTemplate || undefined}
        imageUrlTemplate={imageUrlTemplate || undefined}
        renderAsLink={false}
      />
    );
  }

  const hasImage = Boolean(thumbnail);

  const textContainerClasses = clsx(
    !hasImage && styles.textContainer,
    hasImage && styles.textContainerWithImage,
  );

  const itemBlock = (
    <>
      {thumbnail}
      <div className={textContainerClasses}>
        {itemTitle && (
          <SmallHeader as="p">
            <span
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: safetyHtml(itemTitle) }}
            />
          </SmallHeader>
        )}
        {[
          itemTextTemplates.itemsInShipmentAttribute0,
          itemTextTemplates.itemsInShipmentAttribute1,
          itemTextTemplates.itemsInShipmentAttribute2,
          itemTextTemplates.itemsInShipmentAttribute3,
        ].map((template) => {
          if (!template || typeof template !== 'string') return null;
          const renderedText = mustache.render(
            template,
            itemInfoPriceFormatted,
            undefined,
            renderOptions,
          );
          return (
            <Paragraph
              key={renderedText}
              containerCustomStyle={{
                margin: '0.25rem 0',
                overflowWrap: 'break-word',
              }}
            >
              {safetyHtml(renderedText)}
            </Paragraph>
          );
        })}
      </div>
    </>
  );

  const itemUrl = mustache.render(
    itemUrlTemplate || '',
    itemInfoPriceFormatted,
    undefined,
    renderOptions,
  );

  const componentEventLog = {
    ['shipments']: {
      clickComponent: NarvarClickSubTypes.SHIPMENTS,
      eventComponent: NarvarEventComponents.NTH_PACK_SHIPMENTS,
    },
    ['itemsInThisShipment']: {
      clickComponent: NarvarClickSubTypes.ITEMS_IN_THIS_SHIPMENT,
      eventComponent: NarvarEventComponents.NTH_PACK_ITEMS_IN_THIS_SHIPMENT,
    },
  };

  return (
    <div className={styles.container}>
      {itemUrl ? (
        <a
          href={itemUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(): void =>
            logEvent({
              clickLinkDestinationUrl: itemUrl,
              clickLinkLabel: itemUrl,
              clickType: NarvarClickTypes.OUTGOING,
              eventType: NarvarEventTypes.CLICK,
              feature: featureName,
              ...(featureName === 'shipments' ||
              featureName === 'itemsInThisShipment'
                ? {
                    clickCategory:
                      componentEventLog[featureName].clickComponent,
                    clickSubType: componentEventLog[featureName].clickComponent,
                    eventComponent:
                      componentEventLog[featureName].eventComponent,
                    eventFeature: componentEventLog[featureName].eventComponent,
                    pageComponent:
                      componentEventLog[featureName].eventComponent,
                  }
                : {}),
            })
          }
        >
          {itemBlock}
        </a>
      ) : (
        itemBlock
      )}
    </div>
  );
};

export default ItemTile;
